import { useState } from "react";
import { Plus } from "lucide-react";
import { personPhotoUrl } from "../../lib/queries/photos";
import styles from "./MemberPhoto.module.css";
import type { ID } from "../../lib/id";

// The round photo chip at the head of a roster row.
//
// Three states, and which one shows is not only about whether a photo
// exists:
//
//   - on file        → the photo itself (clickable when editable: the same
//                      dialog is where it is replaced)
//   - missing, may add   → a dashed circle with a plus; a real button
//   - missing, may not   → the same dashed circle, inert and unfocusable
//
// The photo is fetched from the cookie-authenticated
// GET /persons/:id/photo — it is PII (often minors), so it never has a
// public URL and this is an <img src> rather than anything cached by URL
// elsewhere.

export interface MemberPhotoProps {
  personId: ID;
  firstName: string;
  lastName: string;
  hasPhoto: boolean;
  /** Supplied only when the viewer may change the photo. */
  onAdd?: () => void;
  /**
   * Bumped by the caller after an upload. The photo URL is stable, so
   * without it the browser happily re-serves the bytes it already has and
   * a replaced photo appears not to have changed.
   */
  version?: number;
  /** Diameter in px; the roster default is 34. */
  size?: number;
}

export function MemberPhoto({
  personId,
  firstName,
  lastName,
  hasPhoto,
  onAdd,
  version = 0,
  size,
}: MemberPhotoProps) {
  const sized = size != null ? { width: size, height: size } : undefined;
  // A photo the server has forgotten (or that fails to decode) must not
  // leave a broken-image glyph in the roster — fall back to the empty state.
  const [failed, setFailed] = useState(false);
  const name = `${firstName} ${lastName}`.trim();

  if (hasPhoto && !failed) {
    const img = (
      <img
        className={styles.photo}
        style={sized}
        src={personPhotoUrl(personId, { size: "thumb", version })}
        alt={`Fotka: ${name}`}
        // Off the paint path even at thumbnail size: a roster is dozens of
        // these, and a photo stored before thumbnails existed is served as
        // the original until its copy has been made.
        decoding="async"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
    // A photo already on file is still worth clicking: the same dialog is
    // where it gets replaced. Leaving it inert would mean a wrong photo
    // could never be corrected from the roster at all.
    if (!onAdd) return img;
    return (
      // eslint-disable-next-line @marekl/adminer/prefer-lib-form-controls -- a round photo chip: `Button` has no shape between its own radius and a 32px circle wrapping an `<img>`, and no avatar slot. (`Avatar` takes `src` since 0.34.0 and should own the circle — but it is decorative, so the clickable wrapper is still ours.) (§25)
      <button
        type="button"
        className={styles.photoButton}
        onClick={onAdd}
        title={`Změnit fotku: ${name}`}
        aria-label={`Změnit fotku: ${name}`}
      >
        {img}
      </button>
    );
  }

  if (!onAdd) {
    return (
      <span className={styles.empty} style={sized} role="img" aria-label={`Chybí foto: ${name}`} />
    );
  }

  return (
    // eslint-disable-next-line @marekl/adminer/prefer-lib-form-controls -- the empty affordance: a dashed circle with a plus, sized to the photo chip beside it. `Button` brings its own face and radius; nothing here is a labelled action (§25)
    <button
      type="button"
      className={styles.empty}
      style={sized}
      onClick={onAdd}
      title={`Nahrát fotku: ${name}`}
      aria-label={`Nahrát fotku: ${name}`}
    >
      <Plus size={16} aria-hidden />
    </button>
  );
}
